"use client"

/**
 * Supplementary insurance may cover part of it. The v4 homepage's section
 * (Figma 314:10935), shared so every main page of the membership-first site
 * shows insurance the same way. Copy comes in as props; the insurers are the
 * CMS's active CH insurers with a logo (`getHomeV4Insurers`, server-side).
 *
 * Real insurer logos, dealt round-robin into three full-bleed marquee rows
 * that scroll at different speeds and alternating directions — so they drift
 * relative to each other rather than marching in lockstep, per Eugene's brief
 * (03.09).
 *
 * A local rail, not the shared `LogoMarquee`: that component hardcodes one
 * scroll direction. Same proven mechanic though — `scroll-marquee-25` plus 4
 * duplicated copies — see the shared component's own comment for why: at 2
 * copies the loop visibly snaps at the seam (AHD-6405).
 */

import Image from "next/image"
import Link from "next/link"
import { buttonVariants } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export type InsurerLogo = { name: string; logo: string }

/**
 * Each row loops its OWN share of the insurers, dealt round-robin (5/5/4 of
 * 14), so a logo only ever repeats inside its row. Putting the whole set in
 * every row spaced the repeats further apart but showed each logo three
 * times on screen at once, one per row — Eugene: "праздник повторок".
 *
 * A marquee cannot have no repeats: the loop IS a repeat. What three unique
 * rows would look like is ~700px of pills each, centred, static — a
 * different section. `ROWS.length` is the one knob: two rows would put seven
 * per row and halve how often a repeat is in view.
 *
 * One copy is ~750px, so ~42s per copy is the ~18px/s drift asked for.
 */
const ROWS: { duration: number; reverse: boolean }[] = [
  { duration: 42, reverse: false },
  { duration: 50, reverse: true },
  { duration: 36, reverse: false },
]

function InsurerRail({
  insurers,
  duration,
  reverse,
  grey,
}: {
  insurers: InsurerLogo[]
  duration: number
  reverse: boolean
  grey: boolean
}) {
  const fade = grey ? "from-background" : "from-white"
  return (
    <div className="relative overflow-hidden">
      <div
        className={cn(
          "pointer-events-none absolute top-0 bottom-0 left-0 z-10 w-24 bg-gradient-to-r to-transparent md:w-44",
          fade
        )}
      />
      <div
        className={cn(
          "pointer-events-none absolute top-0 right-0 bottom-0 z-10 w-24 bg-gradient-to-l to-transparent md:w-44",
          fade
        )}
      />
      <div
        className="flex w-max items-center"
        // Longhands, not the `animation` shorthand next to `animationDirection`:
        // React warns when a shorthand and one of its parts share a style
        // object, because a rerender can apply them in either order.
        style={{
          animationName: "scroll-marquee-25",
          animationDuration: `${duration}s`,
          animationTimingFunction: "linear",
          animationIterationCount: "infinite",
          animationDirection: reverse ? "reverse" : "normal",
        }}
      >
        {[...insurers, ...insurers, ...insurers, ...insurers].map(
          (insurer, i) => (
            /* Figma 307:7941 — the pill: neutral-50, radius 999, a 48px circle
               with the logo in colour, 18px, then the name. The file pads it
               12px (74 tall); Eugene's call is 8px (64) — the thicker ring
               read as a taller pill than the row wanted. Each pill carries a 10px margin-right (5 + 5 in the file)
               so the rail is an exact multiple of one copy and the -25% loop
               lands on a copy boundary. */
            <div
              key={`${insurer.name}-${i}`}
              className={cn(
                "mr-[10px] flex h-16 shrink-0 items-center gap-[18px] rounded-full p-2",
                grey ? "bg-white" : "bg-neutral-50"
              )}
            >
              <span
                className={cn(
                  "flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-full",
                  grey ? "bg-neutral-50" : "bg-white"
                )}
              >
                <Image
                  src={insurer.logo}
                  alt=""
                  width={48}
                  height={48}
                  unoptimized
                  className="size-8 object-contain"
                />
              </span>
              <span className="type-body-lg-emphasized pr-2 whitespace-nowrap text-neutral-950">
                {insurer.name}
              </span>
            </div>
          )
        )}
      </div>
    </div>
  )
}

export function InsurerMarqueeSection({
  section,
  eyebrow,
  heading,
  body,
  ctaLabel,
  ctaHref,
  insurers,
  tone = "white",
  onCtaClick,
}: {
  /** Container class for the copy and the CTA. */
  section: string
  eyebrow: string
  heading: string
  body: string
  ctaLabel: string
  ctaHref: string
  insurers: InsurerLogo[]
  /** `grey`: the page background with white pills, for a page where the
      section above is white (the plan site's pricing, Eugene 02.10). */
  tone?: "white" | "grey"
  /** Runs on a click on the CTA, for its analytics event. */
  onCtaClick?: () => void
}) {
  const grey = tone === "grey"
  const rows = ROWS.map((cfg, i) => ({
    ...cfg,
    insurers: insurers.filter((_, idx) => idx % ROWS.length === i),
  }))

  return (
    <section
      className={cn(
        "overflow-hidden py-10 md:pt-[92px] md:pb-[107px]",
        grey ? "bg-background" : "bg-white"
      )}
    >
      <div className={`${section} text-center`}>
        <p className="type-label-lg text-neutral-500">{eyebrow}</p>
        <h2 className="type-display-md-emphasized mx-auto mt-3 max-w-[760px] text-balance text-neutral-900 md:mt-6">
          {heading}
        </h2>
        <p className="type-body-xl mx-auto mt-6 max-w-[483px] text-neutral-600">
          {body}
        </p>
      </div>

      {/* Full-bleed: the Figma pill cloud was content-width, but a marquee
          reads as a marquee only when it runs to the viewport edge. */}
      {rows.some((r) => r.insurers.length > 0) && (
        <div
          className="mt-8 flex flex-col gap-[15px] md:mt-[41px]"
          style={{
            marginLeft: "calc(50% - 50vw)",
            marginRight: "calc(50% - 50vw)",
          }}
        >
          {rows.map(
            (row, i) =>
              row.insurers.length > 0 && (
                <InsurerRail
                  key={i}
                  insurers={row.insurers}
                  duration={row.duration}
                  reverse={row.reverse}
                  grey={grey}
                />
              )
          )}
        </div>
      )}

      <div className={`${section} mt-12 flex justify-center md:mt-[50px]`}>
        <Link
          href={ctaHref}
          onClick={onCtaClick}
          className={cn(
            buttonVariants({ variant: "primary" }),
            "bg-neutral-900 hover:bg-neutral-800"
          )}
        >
          {ctaLabel}
        </Link>
      </div>
    </section>
  )
}
